import {
    buildDownloadsChartRows,
    downloadsSeriesColor,
    formatDownloadsAxisDate,
} from './downloads-form.helpers';
import type { DownloadsSeries } from './downloads-form.helpers';
import { MultilineChart } from './multiline-chart';
import {
    isThemePalette,
    resolvePaletteColors,
    resolveSeriesColors,
} from './palette-colors';
import { DegradationNote } from '../degradation-cause';
import {
    DOWNLOADS_PLATFORM_LABELS,
    DOWNLOADS_PLATFORMS,
} from '~helpers/downloads-composition';
import { useTweenedValue } from '~containers/analytics/components/kpi-tile/use-tweened-value';
import type { KpiFormProps, KpiPalette } from '../types';
import style from './downloads-form.module.scss';

const PLATFORM_LABELS = DOWNLOADS_PLATFORMS.map(
    (platform) => DOWNLOADS_PLATFORM_LABELS[platform]
);

const resolveDownloadsColors = (
    series: DownloadsSeries[],
    palette: KpiPalette | undefined
): (string | undefined)[] | undefined => {
    if (!isThemePalette(palette)) {
        return resolvePaletteColors(series.length, palette);
    }
    const platformColors = resolveSeriesColors(
        PLATFORM_LABELS,
        palette,
        'line'
    );
    return series.map(
        ({ label }) => platformColors?.[PLATFORM_LABELS.indexOf(label)]
    );
};

/** One platform's legend row. Tweens the period total toward its new figure
 * on a data change; `dataCompleteThrough` is a date, not a figure, so it
 * renders as is. */
const DownloadsTotalItem = ({
    series,
    color,
}: {
    series: DownloadsSeries;
    color: string;
}) => {
    const tweenedTotal = useTweenedValue(series.total);

    return (
        <li className={style.downloads_totals_item}>
            <span
                className={style.downloads_totals_dot}
                style={{ background: color }}
            />
            <span className={style.downloads_totals_label}>
                {series.label} -{' '}
                <strong>
                    {Math.round(tweenedTotal).toLocaleString('en-US')}
                </strong>
                {/* The freshness the API declared, not the one the
                    last point implies. It is the only thing that
                    still says how current a platform is when that
                    platform reported no rows at all and its line is
                    nowhere to be seen. */}
                {series.dataCompleteThrough !== undefined && (
                    <span className={style.downloads_totals_through}>
                        {' '}
                        · through{' '}
                        {formatDownloadsAxisDate(series.dataCompleteThrough)}
                    </span>
                )}
            </span>
        </li>
    );
};

/**
 * Downloads form: one daily line per platform over the selected period, plus
 * each platform's period total.
 *
 * The point of the form is that the two lines end where their stores' reporting
 * ends, not where the period ends: Google Play typically lags Apple by about a
 * week, so the Android line stops earlier and the gap between the two endings
 * tells the reader how current each source is. `connectNulls` is off so an
 * unreported day breaks the line instead of being drawn through, and the
 * composition never fills a gap, so a reported 0 still plots as a point on the
 * baseline.
 *
 * `data.missing` is rendered under the legend rather than left in the (i)
 * popover alone: it holds only the platforms that produced no line, because a
 * one-line iOS-only card is otherwise indistinguishable from a card that only
 * ever had one platform, on the card whose entire purpose is comparing the
 * two. The composition promises to draw what it has and say what it is
 * missing; this is where it says it.
 *
 * Deliberately plain: the design for this card lands later and will change the
 * rendering only, not the data it reads.
 */
export const DownloadsForm = ({ data, palette }: KpiFormProps) => {
    if (data.form !== 'downloads') return null;
    const rows = buildDownloadsChartRows(data.range, data.series);
    const seriesColors = resolveDownloadsColors(data.series, palette);
    const coloredSeries = data.series.map((series, index) => ({
        series,
        color: seriesColors?.[index] ?? downloadsSeriesColor(series.label),
    }));

    return (
        <div className={style.downloads_form}>
            <div className={style.downloads_chart}>
                <MultilineChart
                    rows={rows}
                    series={coloredSeries.map(({ series, color }) => ({
                        label: series.label,
                        color,
                    }))}
                    unit="count"
                />
            </div>

            <ul className={style.downloads_totals}>
                {coloredSeries.map(({ series, color }) => (
                    <DownloadsTotalItem
                        key={series.label}
                        series={series}
                        color={color}
                    />
                ))}
            </ul>

            {data.missing?.map((entry) => (
                <p
                    key={entry.reason}
                    className={`${style.downloads_missing} footnote_description`}
                >
                    <DegradationNote
                        cause={entry.cause}
                        reason={entry.reason}
                    />
                </p>
            ))}
        </div>
    );
};
